/**
 * CategoryIcon — pictogramme unifié pour les 9 catégories canoniques RentaScan.
 *
 * Utilisé partout : cartes produits, fiches produit, sélecteur d'ingrédients,
 * hausses/baisses, recettes impactées et lignes de scan de facture. Une seule
 * icône par catégorie, jamais une photo propre au produit.
 *
 * Les anciennes catégories (« Charcuterie », « Fromagerie »…) sont
 * automatiquement remappées via canonicalCategory() vers l'une des 9.
 */
import {
  Beef, Fish, Apple, Milk, ShoppingBasket, Wine, Martini, GlassWater, Package,
} from "lucide-react";
import { canonicalCategory } from "../lib/format";

const MAP = {
  "Viandes":              { Icon: Beef,           bg: "#B23A3A", fg: "#FFFFFF" }, // rouge
  "Produits de la mer":   { Icon: Fish,           bg: "#4FA3D1", fg: "#FFFFFF" }, // bleu clair
  "Fruits et légumes":    { Icon: Apple,          bg: "#3A8C3A", fg: "#FFFFFF" }, // vert
  "Crèmerie":             { Icon: Milk,           bg: "#F5C542", fg: "#0B1F33" }, // jaune
  "Épicerie":             { Icon: ShoppingBasket, bg: "#FF9F1C", fg: "#0B1F33" }, // orange
  "Vins":                 { Icon: Wine,           bg: "#5B1F3A", fg: "#FFD166" }, // bordeaux
  "Spiritueux":           { Icon: Martini,        bg: "#6E4AA6", fg: "#FFFFFF" }, // violet
  "Boissons":             { Icon: GlassWater,     bg: "#1769E0", fg: "#FFFFFF" }, // bleu
  "Divers":               { Icon: Package,        bg: "#8A94A6", fg: "#FFFFFF" }, // gris
};

function pick(category) {
  const canon = canonicalCategory(category);
  return MAP[canon] || MAP["Divers"];
}

export function CategoryIcon({ category, size = 40, className = "", rounded = "md" }) {
  const { Icon, bg, fg } = pick(category);
  const px = typeof size === "number" ? `${size}px` : size;
  const radius = rounded === "full" ? "9999px"
    : rounded === "lg" ? "10px"
    : rounded === "sm" ? "4px"
    : "6px";
  return (
    <div
      className={`shrink-0 ${className}`}
      style={{
        // Feb 2026 §« icône fiche produit décalée » — display / align en
        // inline style pour que les utilitaires Tailwind (`hidden xs:block
        // sm:block` sur ProductDetail) ne puissent PAS écraser le flex
        // centering. Dimensions verrouillées : le pictogramme reste
        // parfaitement centré H+V quel que soit le breakpoint ou la
        // longueur du titre voisin (`flex-1 min-w-0` du parent ne peut
        // plus l'aplatir).
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: bg,
        color: fg,
        width: px,
        height: px,
        minWidth: px,
        minHeight: px,
        borderRadius: radius,
        lineHeight: 1,
        verticalAlign: "middle",
      }}
      role="img"
      aria-label={`Catégorie ${canonicalCategory(category)}`}
      data-testid="category-icon"
    >
      <Icon
        style={{
          width: "58%",
          height: "58%",
          flexShrink: 0,
          display: "block",
        }}
        strokeWidth={2}
      />
    </div>
  );
}

export default CategoryIcon;
